import React from 'react';
import { Box, Flex, Text, IconButton, useDisclosure, Image, keyframes, Icon } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { RiArrowRightUpLine } from 'react-icons/ri';
import { FiCode, FiLayers, FiUser, FiMail } from 'react-icons/fi';

// Animation keyframes
const glowKeyframes = keyframes`
  0% { text-shadow: 0 0 5px rgba(0, 210, 255, 0.3), 0 0 10px rgba(0, 210, 255, 0.2); }
  50% { text-shadow: 0 0 15px rgba(0, 210, 255, 0.5), 0 0 30px rgba(0, 210, 255, 0.3); }
  100% { text-shadow: 0 0 5px rgba(0, 210, 255, 0.3), 0 0 10px rgba(0, 210, 255, 0.2); }
`;

const glowAnimation = `${glowKeyframes} 3s infinite`;
const MotionFlex = motion(Flex);

// Custom hamburger icon
const HamburgerMenu = ({ isOpen }) => {
    return (
        <Box
            position="relative"
            width="24px"
            height="24px"
            transition="all 0.3s ease"
        >
            <Box
                position="absolute"
                top={isOpen ? "11px" : "6px"}
                left="0"
                width="24px"
                height="2px"
                bg="white"
                transform={isOpen ? "rotate(45deg)" : "none"}
                transition="all 0.3s ease"
            />
            <Box
                position="absolute"
                top="11px"
                left="0"
                width={isOpen ? "24px" : "16px"}
                height="2px"
                bg={isOpen ? "white" : "cyan.400"}
                opacity={isOpen ? 0 : 1}
                transition="all 0.3s ease"
            />
            <Box
                position="absolute"
                top={isOpen ? "11px" : "16px"}
                left="0"
                width="24px"
                height="2px"
                bg="white"
                transform={isOpen ? "rotate(-45deg)" : "none"}
                transition="all 0.3s ease"
            />
        </Box>
    );
};

// Inside the Header component
const Header = () => {
    const { isOpen, onToggle } = useDisclosure();

    // Function to handle navigation and close menu
    const handleNavigation = (sectionId) => {
        onToggle(); // Close the menu

        // Scroll to the section
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <>
            {/* Logo in top left */}
            <Flex
                as="header"
                position="fixed"
                top="0"
                left="0"
                p={{ base: "15px", md: "20px" }}
                zIndex="10"
                align="center"
            >
                <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="bold"
                    letterSpacing="wider"
                    bgGradient="linear(to-r, cyan.400, purple.400)"
                    bgClip="text"
                    _hover={{ animation: glowAnimation }}
                    cursor="pointer"
                    fontFamily="Space Grotesk"
                    onClick={() => handleNavigation('home')}
                    display={{ base: "none", md: "block" }}
                >
                    Smith
                </Text>
            </Flex>

            {/* Menu toggle in top right */}
            <Flex
                position="fixed"
                top="0"
                right="0"
                p={{ base: "15px", md: "20px" }}
                zIndex="10"
                align="center"
                justify="flex-end"
            >
                <IconButton
                    icon={<HamburgerMenu isOpen={isOpen} />}
                    variant="ghost"
                    color="white"
                    onClick={onToggle}
                    aria-label="Toggle Menu"
                    _hover={{
                        bg: "transparent",
                        transform: "rotate(90deg)"
                    }}
                    transition="all 0.3s ease"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="transparent"
                    _active={{
                        borderColor: "cyan.400",
                        transform: "scale(0.95)"
                    }}
                />
            </Flex>

            {/* Menu overlay */}
            <Box
                position="fixed"
                top="0"
                left="0"
                right="0"
                bottom="0"
                bg="black"
                zIndex="9"
                opacity={isOpen ? "0.98" : "0"}
                pointerEvents={isOpen ? "all" : "none"}
                transition="all 0.5s cubic-bezier(0.16, 1, 0.3, 1)"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                backdropFilter={isOpen ? "blur(10px)" : "none"}
                transform={isOpen ? "scale(1)" : "scale(0.95)"}
            >
                <Box
                    position="absolute"
                    top={{ base: "15px", md: "20px" }}
                    left={{ base: "15px", md: "20px" }}
                    opacity={isOpen ? 1 : 0}
                    transition="opacity 0.5s ease 0.2s"
                >
                    <Text
                        fontSize={{ base: "lg", md: "xl" }}
                        fontWeight="bold"
                        letterSpacing="wider"
                        bgGradient="linear(to-r, cyan.400, purple.400)"
                        bgClip="text"
                        fontFamily="Space Grotesk"
                    >
                        Smith
                    </Text>
                </Box>

                <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    h="100%"
                    opacity={isOpen ? 1 : 0}
                    transition="opacity 0.5s ease 0.3s"
                >
                    <MotionFlex
                        align="center"
                        mb={8}
                        _hover={{ color: "cyan.400" }}
                        cursor="pointer"
                        transition="all 0.3s ease"
                        whileHover={{ x: 10 }}
                        onClick={() => handleNavigation('home')}
                    >
                        <Icon as={FiCode} mr={4} fontSize={{ base: "2xl", md: "3xl" }} />
                        <Text fontSize={{ base: "3xl", md: "5xl" }} fontWeight="bold" fontFamily="Space Grotesk">Home</Text>
                    </MotionFlex>

                    <MotionFlex
                        align="center"
                        mb={8}
                        _hover={{ color: "cyan.400" }}
                        cursor="pointer"
                        transition="all 0.3s ease"
                        whileHover={{ x: 10 }}
                        onClick={() => handleNavigation('projects')}
                    >
                        <Icon as={FiLayers} mr={4} fontSize={{ base: "2xl", md: "3xl" }} />
                        <Text fontSize={{ base: "3xl", md: "5xl" }} fontWeight="bold" fontFamily="Space Grotesk">Projects</Text>
                    </MotionFlex>

                    <MotionFlex
                        align="center"
                        mb={8}
                        _hover={{ color: "cyan.400" }}
                        cursor="pointer"
                        transition="all 0.3s ease"
                        whileHover={{ x: 10 }}
                        onClick={() => handleNavigation('about')}
                    >
                        <Icon as={FiUser} mr={4} fontSize={{ base: "2xl", md: "3xl" }} />
                        <Text fontSize={{ base: "3xl", md: "5xl" }} fontWeight="bold" fontFamily="Space Grotesk">About</Text>
                    </MotionFlex>

                    <MotionFlex
                        align="center"
                        mb={8}
                        _hover={{ color: "cyan.400" }}
                        cursor="pointer"
                        transition="all 0.3s ease"
                        whileHover={{ x: 10 }}
                        onClick={() => handleNavigation('contact')}
                    >
                        <Icon as={FiMail} mr={4} fontSize={{ base: "2xl", md: "3xl" }} />
                        <Text fontSize={{ base: "3xl", md: "5xl" }} fontWeight="bold" fontFamily="Space Grotesk">Contact</Text>
                    </MotionFlex>
                </Flex>

                {/* Avatar - Only show on desktop */}
                <Box
                    position="absolute"
                    bottom="40px"
                    right="40px"
                    width="180px"
                    height="180px"
                    opacity={isOpen ? 1 : 0}
                    transition="all 0.5s ease 0.4s"
                    transform={isOpen ? "translateY(0)" : "translateY(20px)"}
                    display={{ base: "none", md: "block" }}
                >
                    <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        _before={{
                            content: '""',
                            position: 'absolute',
                            top: '-2px',
                            left: '-2px',
                            right: '-2px',
                            bottom: '-2px',
                            background: 'linear-gradient(45deg, #00D2FF, #FF00FF)',
                            zIndex: -1,
                            borderRadius: 'md',
                            animation: `${glowKeyframes} 3s infinite`,
                        }}
                    >
                        <Image
                            src="/avatar.png"
                            alt="Smith"
                            borderRadius="md"
                            transition="all 0.3s ease"
                            _hover={{ transform: "scale(1.05)" }}
                        />
                    </Box>
                </Box>

                {/* Grid overlay for aesthetic */}
                <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    opacity="0.05"
                    pointerEvents="none"
                    backgroundImage="linear-gradient(to right, rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 1px, transparent 1px)"
                    backgroundSize="80px 80px"
                    backgroundPosition="center center"
                />
            </Box>
        </>
    );
};

export default Header;